import React, { useState } from 'react';
import { COUNTRY_FLAGS } from '../constants/countries';

export function getCountryFlagCode(emoji: string): string | null {
  if (!emoji) return null;
  
  // Special cases for UK regions (England, Scotland, Wales)
  if (emoji === '🏴󠁧󠁢󠁥󠁮󠁧󠁿') return 'gb-eng';
  if (emoji === '🏴󠁧󠁢󠁳󠁣󠁴󠁿') return 'gb-sct';
  if (emoji === '🏴󠁧󠁢󠁷󠁬󠁳󠁿') return 'gb-wls';

  // Check if it's a standard 2-letter emoji flag
  // Regional indicator symbols are in the range U+1F1E6 to U+1F1FF
  if (emoji.length >= 4) {
    const cp1 = emoji.codePointAt(0);
    const cp2 = emoji.codePointAt(2);
    
    if (cp1 && cp2 && cp1 >= 0x1F1E6 && cp1 <= 0x1F1FF && cp2 >= 0x1F1E6 && cp2 <= 0x1F1FF) {
      const char1 = String.fromCharCode(cp1 - 0x1F1E6 + 97);
      const char2 = String.fromCharCode(cp2 - 0x1F1E6 + 97);
      return char1 + char2;
    }
  }

  return null;
}

interface CountryFlagProps {
  countryName: string;
  className?: string;
  fallbackEmoji?: string;
}

export const CountryFlag: React.FC<CountryFlagProps> = ({ countryName, className = '', fallbackEmoji = '⚽' }) => {
  const [imgError, setImgError] = useState(false);
  const emoji = COUNTRY_FLAGS[countryName] || fallbackEmoji;
  const code = getCountryFlagCode(emoji);

  if (code && !imgError) {
    // Strip width/height classes to let PNG render naturally, and add 2px border radius
    const imgClassName = className
      .replace(/\bw-(?:\[.*?\]|\w+)\b/g, '')
      .replace(/\bh-(?:\[.*?\]|\w+)\b/g, '')
      .trim();

    return (
      <img 
        src={`https://flagcdn.com/w20/${code}.png`} 
        alt={countryName}
        className={`object-contain flex-shrink-0 rounded-[2px] ${imgClassName}`}
        loading="lazy"
        onError={() => setImgError(true)}
      />
    );
  }

  // Fallback to emoji
  return <span className={`flex-shrink-0 leading-none ${className}`}>{emoji}</span>;
};
